import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, FileText, RefreshCw, AlertCircle, Send } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { useUserRole } from "@/hooks/useUserRole";

interface DealConversationProps {
  dealId: string;
}

interface Conversation {
  id: string;
  deal_id: string;
  user_id: string;
  message: string;
  message_type: string;
  is_internal: boolean;
  created_at: string;
  profiles?: {
    name: string;
  };
}

const messageTypeIcons = {
  comment: MessageCircle,
  status_update: RefreshCw,
  document_upload: FileText,
  approval_request: AlertCircle,
};

export const DealConversation = ({ dealId }: DealConversationProps) => {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("comment");
  const [isInternal, setIsInternal] = useState(false);

  // Fetch conversations
  const { data: conversations, isLoading } = useQuery({
    queryKey: ["deal-conversations", dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deal_conversations" as any)
        .select(`
          *,
          profiles:user_id (
            name
          )
        `)
        .eq("deal_id", dealId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as unknown as Conversation[];
    },
  });

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel(`deal-conversations-${dealId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "deal_conversations",
          filter: `deal_id=eq.${dealId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["deal-conversations", dealId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dealId, queryClient]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversations]);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (data: { message: string; message_type: string; is_internal: boolean }) => {
      const { error } = await supabase.from("deal_conversations" as any).insert({
        deal_id: dealId,
        user_id: user!.id,
        message: data.message,
        message_type: data.message_type,
        is_internal: data.is_internal,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      setMessage("");
      toast.success("Message sent");
      queryClient.invalidateQueries({ queryKey: ["deal-conversations", dealId] });
    },
    onError: () => {
      toast.error("Failed to send message");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    sendMessageMutation.mutate({
      message: message.trim(),
      message_type: messageType,
      is_internal: isInternal,
    });
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading conversations...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Deal Conversation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScrollArea ref={scrollRef} className="h-[400px] pr-4">
          <div className="space-y-4">
            {conversations?.map((conv) => {
              const Icon = messageTypeIcons[conv.message_type as keyof typeof messageTypeIcons] || MessageCircle;
              return (
                <div key={conv.id} className="flex gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {conv.profiles?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">
                        {conv.profiles?.name || "Unknown User"}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        <Icon className="w-3 h-3 mr-1" />
                        {conv.message_type}
                      </Badge>
                      {conv.is_internal && (
                        <Badge variant="secondary" className="text-xs">
                          Internal
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(conv.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap">
                      {conv.message}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <form onSubmit={handleSubmit} className="space-y-3 border-t pt-4">
          <div className="flex gap-2">
            <Select value={messageType} onValueChange={setMessageType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="comment">Comment</SelectItem>
                <SelectItem value="status_update">Status Update</SelectItem>
                <SelectItem value="document_upload">Document Upload</SelectItem>
                <SelectItem value="approval_request">Approval Request</SelectItem>
              </SelectContent>
            </Select>

            {isAdmin() && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="internal"
                  checked={isInternal}
                  onCheckedChange={(checked) => setIsInternal(checked as boolean)}
                />
                <Label htmlFor="internal" className="text-sm">Internal Note</Label>
              </div>
            )}
          </div>

          <Textarea
            placeholder="Type your message... (Ctrl+Enter to send)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) {
                handleSubmit(e);
              }
            }}
            rows={3}
          />

          <Button type="submit" disabled={!message.trim() || sendMessageMutation.isPending}>
            <Send className="w-4 h-4 mr-2" />
            Send Message
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
